// Agents (Console D, views/agents.js `#/agents`; APS-197, APS-310; D22, D45, D78, D98): what the
// coding agents did with this project, and what waits for a person: the pull requests in review
// from a handoff, the handoffs, every call an agent made (the audit log, owners and admins),
// who is connected and which tools they use (APS-300's summary). Configuration (connecting,
// grants, tokens, the handoff) is in Settings › Agents and tokens.
// A client's name comes from the client (a CIMD URL, an MCP client's name): text, never a link.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import { isApiError } from '../api/client'
import {
  agentSummaryQuery,
  alertDestinationsQuery,
  fetchAudit,
  handoffsQuery,
  membersQuery,
  orgQuery,
  projectsQuery,
} from '../api/queries'
import type { AgentSummary, AuditEntry, Handoff, Member } from '../api/types'
import {
  Bars,
  Btn,
  btnClass,
  copyText,
  EmptyNote,
  Hbar,
  LK,
  PageHead,
  Section,
  Status,
  Strip,
  type StripItem,
  Table,
  TableFoot,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import {
  type CallTarget,
  callOutcome,
  callRoute,
  callTarget,
  change,
  isActive,
} from '../lib/agentCalls'
import { sandboxFor } from '../sandbox'
import { ACTION_KEY, describe, who } from './Activity'
import { HandoffsTable, handoffWhere } from './HandoffSettings'
import { useProjectParams } from './params'
import { SandboxAgents } from './SandboxAgents'

// Production's MCP server; a staging console is built with its own (VITE_MCP_URL, APS-478).
export const MCP_URL = import.meta.env.VITE_MCP_URL || 'https://mcp.apsio.io/mcp'
export const MCP_COMMAND = `claude mcp add --transport http apsio ${MCP_URL}`
export const DOCS_URL = 'https://docs.apsio.io/agents'

const VIA = new Set(['mcp', 'oauth', 'token'])
const WEEK = 7

/**
 * The page, or in the sandbox, which has no grants, tokens or audit log of its own, a token to
 * try the CLI or MCP on it (D105).
 */
export function AgentsPage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxAgents /> : <AgentsView />
}

/** An agent, as the mock draws it: its glyph and its name, as text. */
export function AgentName({ name, className }: { name: ReactNode; className?: string }) {
  return (
    <span className={className ?? 'ag-who'}>
      <span className={className === 'se-who' ? 'se-ag' : 'ag-ic'}>
        <Icon name="agent" />
      </span>
      <span translate="no">{name}</span>
    </span>
  )
}

type Client = AgentSummary['clients'][number]

/** A summary client's name: its grant's or token's name, what the MCP server said, its id. */
export const clientName = (c: Client) => c.name || c.label || c.id

/** Each client's display name, by `kind:id`, as Connected shows it. */
const clientNames = (s: AgentSummary | undefined) =>
  new Map((s?.clients ?? []).map((c) => [`${c.kind}:${c.id}`, clientName(c)]))

/** How a client called: MCP, OAuth or a project token, and for a user's own token, who. */
function clientVia(c: Client, members: Member[] | undefined, t: Translator['t']): string {
  const via = c.last_call.via
  const parts = [VIA.has(via) ? t(`agents.via.${via}` as MessageId) : via]
  if (c.kind === 'oauth' && via !== 'oauth') parts.push(t('agents.via.oauth'))
  if (c.kind === 'token') parts.push(t('agents.tokenEnd', { id: c.id.slice(0, 8) }))
  if (c.kind === 'user') {
    const m = members?.find((x) => x.user_id === c.id)
    if (m) parts.push(m.name || m.email)
  }
  return parts.join(' · ')
}

function AgentsView() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const { toast, show } = useToast()
  const org = useQuery(orgQuery(orgId))
  const projects = useQuery(projectsQuery(orgId))
  const week = useQuery(agentSummaryQuery(orgId, projectId, WEEK))
  const twoWeeks = useQuery(agentSummaryQuery(orgId, projectId, WEEK * 2))
  const handoffs = useQuery(handoffsQuery(orgId, projectId))
  // The window starts at a whole minute, so the query key stays put while the page is open.
  const [since] = useState(() =>
    new Date(Math.floor(Date.now() / 60_000) * 60_000 - WEEK * 86_400_000).toISOString(),
  )
  const inWeek = useQuery(handoffsQuery(orgId, projectId, since))
  const destinations = useQuery(alertDestinationsQuery(orgId))
  const members = useQuery(membersQuery(orgId))
  const admin = org.data?.role === 'owner' || org.data?.role === 'admin'
  const project = projects.data?.find((p) => p.id === projectId)?.name
  // Agent handoff off on this Apsio (503): no handoff cells, never a zero.
  const handoffOn = !(handoffs.isError && isApiError(handoffs.error, 503))
  const handed = handoffs.data?.handoffs ?? []
  const waiting = handed.filter((h) => h.pull_request?.state === 'in_review')
  const total = (s: AgentSummary | undefined, days: AgentSummary['days'] | undefined) =>
    s && days ? days.reduce((n, d) => n + d.calls, 0) : undefined
  const calls = total(week.data, week.data?.days)
  const before = total(twoWeeks.data, twoWeeks.data?.days.slice(0, WEEK))
  const delta = calls !== undefined && before !== undefined ? change(before, calls) : null
  // The window's handoffs: the API's uncapped total, and open and merged among them.
  const week7 = inWeek.data?.handoffs ?? []
  const merged = week7.filter((h) => h.pull_request?.state === 'merged').length
  const open = week7.filter(
    (h) =>
      h.state !== 'failed' &&
      h.pull_request?.state !== 'merged' &&
      h.pull_request?.state !== 'closed',
  ).length
  const settings = { orgId, projectId, section: 'agents' as const }

  const strip: StripItem[] = []
  if (handoffs.data && inWeek.data) {
    strip.push({
      dt: t('agents.strip.waiting'),
      dd: number(waiting.length),
      d: waiting[0]?.pull_request
        ? t('agents.strip.waitingPr', { n: waiting[0].pull_request.number })
        : t('agents.strip.waitingNone'),
    })
    strip.push({
      dt: t('agents.strip.handoffs'),
      dd: number(inWeek.data.total),
      d: t('agents.strip.handoffsD', { open, merged }),
      link: (p) => <Link {...p} to="/o/$orgId/p/$projectId/settings/$section" params={settings} />,
    })
  }
  if (calls !== undefined)
    strip.push({
      dt: t('agents.strip.calls'),
      dd: number(calls),
      d:
        delta === null ? (
          t('agents.strip.callsFirst')
        ) : (
          <>
            <span className={delta > 0 ? 'ag-up' : undefined}>
              {t('agents.strip.delta', { delta: `${delta > 0 ? '+' : ''}${number(delta)}` })}
            </span>{' '}
            {t('agents.strip.vsBefore')}
          </>
        ),
    })

  const connected = week.data?.clients.length
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('agents.title')}
        meta={[
          project,
          connected === undefined ? undefined : t('agents.meta.connected', { count: connected }),
          t('agents.meta.days', { days: WEEK }),
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {/* The page's one primary (brand v2): connecting an agent. */}
            <Link
              className={btnClass('solid')}
              to="/o/$orgId/p/$projectId/settings/$section"
              params={settings}
            >
              {t('agents.connect')}
            </Link>
            <Btn
              onClick={async () =>
                show((await copyText(MCP_COMMAND)) ? t('agents.mcpCopied') : t('issues.copyFailed'))
              }
            >
              <Icon name="copy" />
              {t('agents.copyMcp')}
            </Btn>
            <a className={btnClass('ghost')} href={DOCS_URL}>
              {t('agents.docs')}
            </a>
          </>
        }
      />
      {strip.length > 0 ? <Strip label={t('agents.strip')} items={strip} /> : null}
      <div className="grid2">
        <div className="sections">
          {waiting.length > 0 ? (
            <Waiting
              orgId={orgId}
              projectId={projectId}
              waiting={waiting}
              where={
                new Map((destinations.data?.destinations ?? []).map((d) => [d.id, handoffWhere(d)]))
              }
            />
          ) : null}
          {handoffOn ? (
            <Section
              title={t('agents.handoffs')}
              meta={t('agents.handoffsMeta')}
              right={
                <Link
                  className={btnClass('ghost', 'sm')}
                  to="/o/$orgId/p/$projectId/settings/$section"
                  params={settings}
                >
                  {t('agents.rule')}
                </Link>
              }
            >
              {handoffs.isPending ? (
                <Loading />
              ) : handoffs.isError ? (
                <ErrorState error={handoffs.error} retry={() => handoffs.refetch()} />
              ) : handed.length === 0 ? (
                <EmptyNote title={t('agents.handoffsNone')}>
                  {t('agents.handoffsNoneBody')}
                </EmptyNote>
              ) : (
                <HandoffsTable
                  orgId={orgId}
                  projectId={projectId}
                  handoffs={handed}
                  destinations={destinations.data?.destinations ?? []}
                />
              )}
            </Section>
          ) : null}
          {org.isPending ? null : admin ? (
            <Calls
              orgId={orgId}
              projectId={projectId}
              members={members.data}
              names={clientNames(week.data)}
            />
          ) : (
            <Section title={t('agents.calls')}>
              <EmptyNote title={t('agents.activityAdmins')} />
            </Section>
          )}
        </div>
        <div className="sections">
          <Connected summary={week} members={members.data} />
          {week.data && calls ? <PerDay summary={week.data} total={calls} /> : null}
          {week.data && calls ? <Tools summary={week.data} total={calls} /> : null}
        </div>
      </div>
      {toast}
    </main>
  )
}

/** The pull requests a handoff led to that wait for a person's review. */
function Waiting({
  orgId,
  projectId,
  waiting,
  where,
}: {
  orgId: string
  projectId: string
  waiting: Handoff[]
  where: Map<string, string>
}) {
  const { t, relative } = useI18n()
  return (
    <Section title={t('agents.waiting')} meta={t('agents.waitingMeta', { count: waiting.length })}>
      {waiting.map((h) => {
        const pr = h.pull_request
        if (!pr) return null
        const url = /^https:\/\//.test(pr.url) ? pr.url : undefined
        const repo = where.get(h.destination_id)
        return (
          <div className="ag-wait" key={h.id}>
            <span className="tt">
              <b translate="no">
                {t('agents.waitingPr', { n: pr.number })} · {h.subject}
              </b>
              <span>
                {h.issue_id ? (
                  <>
                    {t('agents.waitingFrom')}{' '}
                    <Link
                      className="ul"
                      to="/o/$orgId/p/$projectId/issues/$issueId"
                      params={{ orgId, projectId, issueId: h.issue_id }}
                    >
                      <code translate="no">{h.issue_id}</code>
                    </Link>
                    {' · '}
                  </>
                ) : null}
                {repo ? (
                  <>
                    <code translate="no">{repo}</code>
                    {' · '}
                  </>
                ) : null}
                <span style={{ whiteSpace: 'nowrap' }}>
                  {t('agents.handedOff', { when: relative(h.created_at) })}
                </span>
              </span>
            </span>
            <span className="ac">
              {h.issue_id ? (
                <Link
                  className={btnClass()}
                  to="/o/$orgId/p/$projectId/issues/$issueId"
                  params={{ orgId, projectId, issueId: h.issue_id }}
                >
                  {t('agents.theIssue')}
                </Link>
              ) : null}
              {url ? (
                <a
                  className={btnClass('solid')}
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Icon name="ext" />
                  {t('agents.reviewOnGitHub')}
                </a>
              ) : null}
            </span>
          </div>
        )
      })}
    </Section>
  )
}

/** Every call an agent made in this project, newest first, with its outcome (owners, admins). */
function Calls({
  orgId,
  projectId,
  members,
  names,
}: {
  orgId: string
  projectId: string
  members: Member[] | undefined
  names: Map<string, string>
}) {
  const { t } = useI18n()
  const filter = { kind: 'agent' as const, projectId }
  const log = useInfiniteQuery({
    queryKey: ['org', orgId, 'audit', filter],
    queryFn: ({ pageParam }) => fetchAudit(orgId, pageParam, filter),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.length === 50 ? last.at(-1)?.id : undefined),
  })
  const entries = log.data?.pages.flat() ?? []
  return (
    <Section
      title={t('agents.calls')}
      meta={t('agents.callsMeta')}
      right={
        <Link
          className={btnClass('ghost', 'sm')}
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'activity' }}
        >
          {t('agents.fullLog')}
        </Link>
      }
    >
      {log.isPending ? (
        <Loading />
      ) : log.isError ? (
        <ErrorState error={log.error} retry={() => log.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyNote title={t('agents.activityNone')} />
      ) : (
        <>
          <Table label={t('agents.activityTable')} className="ag-calls" rowKeys>
            <thead>
              <tr>
                <th className="f">{t('activity.when')}</th>
                <th>{t('agents.col.agent')}</th>
                <th>{t('agents.col.call')}</th>
                <th>{t('agents.col.target')}</th>
                <th className="last">{t('agents.col.result')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <CallRow
                  key={e.id}
                  e={e}
                  orgId={orgId}
                  projectId={projectId}
                  members={members}
                  names={names}
                />
              ))}
            </tbody>
          </Table>
          {log.hasNextPage ? (
            <TableFoot end>
              <Btn
                variant="ghost"
                size="sm"
                disabled={log.isFetchingNextPage}
                onClick={() => log.fetchNextPage()}
              >
                {t('agents.earlier')}
              </Btn>
            </TableFoot>
          ) : null}
        </>
      )}
    </Section>
  )
}

/** One audit entry of an agent: a call (its tool or route, what it was about, its outcome) or a
 * change it made. */
function CallRow({
  e,
  orgId,
  projectId,
  members,
  names,
}: {
  e: AuditEntry
  orgId: string
  projectId: string
  members: Member[] | undefined
  names: Map<string, string>
}) {
  const i = useI18n()
  const text = (v: unknown) => (typeof v === 'string' ? v : '')
  const via = text(e.metadata.via)
  const token = text(e.metadata.token_id)
  const oauth = text(e.metadata.oauth_client_id)
  // One name per agent: the one Connected shows (its grant's or token's name), else what the
  // MCP server said it is, else its id.
  const client =
    (oauth && names.get(`oauth:${oauth}`)) ||
    (token && names.get(`token:${token}`)) ||
    text(e.metadata.client) ||
    oauth ||
    (token ? i.t('agents.tokenEnd', { id: token.slice(0, 8) }) : '')
  const call = e.action === 'agent.call'
  const route = text(e.metadata.route)
  const tool = text(e.metadata.tool)
  // A call without the MCP server shows its route as the call; its target is then the object only.
  const target = call ? callTarget(route) : undefined
  const sub = [
    VIA.has(via) ? i.t(`agents.via.${via}` as MessageId) : undefined,
    e.actor_kind === 'token' ? undefined : who(e, members, i.t),
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <tr>
      <td
        className="f faint"
        title={i.date(e.created_at, { dateStyle: 'medium', timeStyle: 'short' })}
      >
        {i.relative(e.created_at)}
      </td>
      <td>
        {client ? <AgentName name={client} /> : <span>{sub}</span>}
        {client ? <span className="t2">{sub}</span> : null}
      </td>
      <td>
        <code translate="no">
          {call ? tool || callRoute(route) : ACTION_KEY[e.action] ? describe(e, i) : e.action}
        </code>
      </td>
      <td>
        <Target
          target={!tool && target?.kind === 'path' ? { kind: 'none' } : target}
          entry={e}
          orgId={orgId}
          projectId={projectId}
        />
      </td>
      <td className="last">
        {call ? <Outcome status={e.status} /> : <Status s="ok" label={i.t('agents.result.done')} />}
      </td>
    </tr>
  )
}

const short = (id: string) => (id.length > 16 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id)

/** What a call was about: the issue, session or trace it read, else its path; a write's issue. */
function Target({
  target,
  entry,
  orgId,
  projectId,
}: {
  target: CallTarget | undefined
  entry: AuditEntry
  orgId: string
  projectId: string
}) {
  const issueId = !target && entry.target_type === 'issue' ? entry.target_id : undefined
  if (issueId || target?.kind === 'issue')
    return (
      <Link
        className={LK}
        to="/o/$orgId/p/$projectId/issues/$issueId"
        params={{ orgId, projectId, issueId: issueId ?? (target as { id: string }).id }}
        translate="no"
      >
        {short(issueId ?? (target as { id: string }).id)}
      </Link>
    )
  if (target?.kind === 'session')
    return (
      <Link
        className={LK}
        to="/o/$orgId/p/$projectId/sessions/$sessionId"
        params={{ orgId, projectId, sessionId: target.id }}
        translate="no"
      >
        {short(target.id)}
      </Link>
    )
  if (target?.kind === 'trace')
    return (
      <Link
        className={LK}
        to="/o/$orgId/p/$projectId/traces/$traceId"
        params={{ orgId, projectId, traceId: target.id }}
        translate="no"
      >
        {short(target.id)}
      </Link>
    )
  if (target?.kind === 'path') return <code translate="no">{target.path}</code>
  return <span className="faint">—</span>
}

/** A call's outcome: answered, refused (a grant, a scope, a limit) or failed, with its status. */
function Outcome({ status }: { status: number | null | undefined }) {
  const { t } = useI18n()
  const o = callOutcome(status)
  if (o === 'pending') return <span className="faint">{t('agents.result.pending')}</span>
  const s = o === 'ok' ? 'ok' : o === 'refused' ? 'miss' : 'fire'
  return <Status s={s} label={t(`agents.result.${o}`, { status: status ?? 0 })} />
}

/** The clients that called this project in the last week, the latest first. */
function Connected({
  summary,
  members,
}: {
  summary: ReturnType<typeof useQuery<AgentSummary>>
  members: Member[] | undefined
}) {
  const { t, number, relative } = useI18n()
  const now = new Date()
  const clients = summary.data?.clients ?? []
  return (
    <Section title={t('agents.connected')} meta={summary.data ? number(clients.length) : undefined}>
      {summary.isPending ? (
        <Loading />
      ) : summary.isError ? (
        <ErrorState error={summary.error} retry={() => summary.refetch()} />
      ) : clients.length === 0 ? (
        <EmptyNote title={t('agents.connectedNone')}>{t('agents.connectedNoneBody')}</EmptyNote>
      ) : (
        clients.map((c) => (
          <div className="ag-on" key={`${c.kind}:${c.id}`}>
            <AgentName name={clientName(c)} />
            <span className="n">
              <b>{number(c.calls)}</b> {t('agents.callsWord', { count: c.calls })}
              <br />
              {relative(c.last_call.at)}
            </span>
            <span className="s">
              {clientVia(c, members, t)}
              {isActive(c.last_call.at, now) ? (
                <>
                  <i className="ag-live" aria-hidden="true" title={t('agents.activeNow')} />
                  <span className="visually-hidden">{t('agents.activeNow')}</span>
                </>
              ) : null}
            </span>
          </div>
        ))
      )}
    </Section>
  )
}

/** Calls per UTC day over the last week. */
function PerDay({ summary, total }: { summary: AgentSummary; total: number }) {
  const { t, date, number } = useI18n()
  const values = summary.days.map((d) => d.calls)
  const day = (s: string) =>
    date(`${s}T12:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const first = summary.days[0]
  const last = summary.days.at(-1)
  return (
    <Section title={t('agents.perDay')} meta={t('agents.meta.days', { days: summary.days.length })}>
      <Bars
        values={values}
        height={150}
        hiFrom={values.length - 1}
        format={(v) => number(Math.round(v))}
        xLabels={
          first && last
            ? [
                [0, day(first.day)],
                [values.length - 1, day(last.day)],
              ]
            : []
        }
        label={t('agents.perDayLabel', {
          days: values.length,
          min: number(Math.min(...values)),
          max: number(Math.max(...values)),
          total: number(total),
        })}
      />
    </Section>
  )
}

/** The MCP tools agents called most, and the API called directly. */
function Tools({ summary, total }: { summary: AgentSummary; total: number }) {
  const { t, number } = useI18n()
  const top = summary.tools.slice(0, 8)
  const max = Math.max(...top.map((x) => x.calls), 1)
  return (
    <Section title={t('agents.tools')} meta={t('agents.callsCount', { count: total })}>
      {top.map((x) => (
        <div className="ag-tool" key={x.tool ?? ''}>
          {x.tool ? <code translate="no">{x.tool}</code> : <span>{t('agents.toolApi')}</span>}
          <Hbar share={x.calls / max} />
          <span>{number(x.calls)}</span>
        </div>
      ))}
    </Section>
  )
}
